// The funnel board (Funnels::BoardComponent). Cards drag between lanes, but only
// forward: a lane accepts cards from the lanes before it (and reorders its
// own). A drop posts the lane's new order to FunnelDealDropsController.
// DealChannel moves cards when a deal changes lane or order in another browser.
import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import Sortable, { type SortableEvent } from "sortablejs";
import type { Subscription } from "@rails/actioncable";

import consumer from "../channels/consumer";

const ANIMATION_MS = 150;

interface DealUpdate {
  deal_id: string;
  new_lane_id: string;
  deal_sort: number;
  active: boolean;
}

export default class FunnelController extends Controller<HTMLElement> {
  static override targets = ["lane", "card", "count"];
  static override values = { dropUrl: String };

  declare readonly laneTargets: HTMLElement[];
  declare readonly cardTargets: HTMLElement[];
  declare readonly countTargets: HTMLElement[];
  declare readonly dropUrlValue: string;

  private sortables: Sortable[] = [];
  private subscription?: Subscription;

  override connect(): void {
    this.sortables = this.laneTargets.map((lane, index) =>
      Sortable.create(lane, {
        animation: ANIMATION_MS,
        group: { name: `lane${index}`, put: Array.from({ length: index }, (_, earlier) => `lane${earlier}`) },
        onAdd: (event) => this.drop(event),
        onUpdate: (event) => {
          if (event.oldIndex !== event.newIndex) this.drop(event);
        },
        onSort: () => this.updateCounts(),
      }),
    );

    this.subscription = consumer.subscriptions.create("DealChannel", {
      received: (update: DealUpdate) => this.move(update),
    });
  }

  override disconnect(): void {
    this.sortables.forEach((sortable) => sortable.destroy());
    this.sortables = [];
    this.subscription?.unsubscribe();
  }

  private drop(event: SortableEvent): void {
    const lane = event.to;
    const dealIds = Array.from(lane.querySelectorAll<HTMLElement>("[data-deal-id]"), (card) => card.dataset.dealId);

    post(this.dropUrlValue, {
      body: {
        funnel_deal_drop: {
          deal_id: event.item.dataset.dealId,
          destination_lane_id: lane.dataset.laneId,
          destination_lane_deal_ids_by_sort: dealIds,
        },
      },
      responseKind: "json",
    })
      .then((response) => {
        if (!response.ok) window.location.reload();
      })
      .catch(() => window.location.reload());
  }

  private move({ deal_id: dealId, new_lane_id: laneId, deal_sort: sort, active }: DealUpdate): void {
    const card = this.cardTargets.find((element) => element.dataset.dealId === dealId);
    if (!card) return;

    const lane = this.laneTargets.find((element) => element.dataset.laneId === laneId);
    if (active && lane) {
      const sibling = Array.from(lane.children).filter((child) => child !== card)[sort] ?? null;
      lane.insertBefore(card, sibling);
    } else {
      card.remove();
    }

    this.updateCounts();
  }

  private updateCounts(): void {
    this.laneTargets.forEach((lane, index) => {
      const count = this.countTargets[index];
      if (count) count.textContent = String(lane.querySelectorAll("[data-deal-id]").length);
    });
  }
}
